import type { Cell, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import cx from 'classnames';
import type { ReactNode } from 'react';
import { Fragment } from 'react';

import { getRowSpanValue, shouldSkipCell } from '../../adapters/columnAdapter';
import type { ColumnWidthMap } from '../../helpers';
import { getOriginalRowData } from '../../helpers';
import type { TanStackColumnMeta } from '../../types';
import TableCell from '../TableCell';

interface RowAttributes {
  disabled?: boolean;
  expanded?: boolean;
}

interface TableRowProps<T extends object> {
  fixedColumnOffsets: Record<string, number>;
  isNested?: boolean;
  isSelected?: boolean;
  renderExpandIcon?: (row: Row<T>) => ReactNode;
  renderExpandedRow?: (record: T, index: number) => ReactNode;
  row: Row<T>;
  rowIndex: number;
  tableId: string;
  totalRows: number;
  widths: ColumnWidthMap;
}

function renderCellContent<T extends object>(
  cell: Cell<T, unknown>,
  row: Row<T>,
  renderExpandIcon?: (row: Row<T>) => ReactNode
): ReactNode {
  const cellMeta = cell.column.columnDef.meta as TanStackColumnMeta<T> | undefined;
  const columnKey = cellMeta?.columnKey || cell.column.id;
  const isExpandColumn = columnKey === 'expandColumn';

  if (isExpandColumn && renderExpandIcon) {
    return renderExpandIcon(row);
  }

  return flexRender(cell.column.columnDef.cell, cell.getContext());
}

function TableRowComponent<T extends object>(props: TableRowProps<T>): ReactNode {
  const {
    fixedColumnOffsets,
    isNested = false,
    isSelected = false,
    renderExpandIcon,
    renderExpandedRow,
    row,
    rowIndex,
    tableId,
    totalRows,
    widths
  } = props;

  const rowData = row.original as RowAttributes;
  const rowClassNames = cx({
    'opacity-[0.24]': rowData.disabled,
    'bg-grey-lightest': rowData.expanded
  });

  const cells = row.getVisibleCells();
  const hasCustomExpandedContent = renderExpandedRow && row.getIsExpanded() && row.depth === 0;

  const lastLeftFixedIndex: number = cells.reduce(
    (acc, cell, index) => ((cell.column.columnDef.meta as TanStackColumnMeta<T> | undefined)?.fixed === 'left' ? index : acc),
    -1
  );
  const firstRightFixedIndex: number = cells.findIndex(
    (cell) => (cell.column.columnDef.meta as TanStackColumnMeta<T> | undefined)?.fixed === 'right'
  );

  const renderCell = (cell: Cell<T, unknown>, cellIndex: number, skipRowSpan = false) => {
    const cellMeta = cell.column.columnDef.meta as TanStackColumnMeta<T> | undefined;
    const columnKey = cellMeta?.columnKey || cell.column.id;
    const rowSpans = cellMeta?.rowSpans;
    const fixedDirection = cellMeta?.fixed;
    const fixedOffset = fixedDirection ? fixedColumnOffsets[columnKey] || 0 : 0;
    const isFixedBoundary: boolean =
      (fixedDirection === 'left' && cellIndex === lastLeftFixedIndex) ||
      (fixedDirection === 'right' && cellIndex === firstRightFixedIndex);

    // Skip cell if it's part of a row span (merged cell) - only for normal rows
    if (!skipRowSpan && shouldSkipCell(rowSpans, rowIndex)) {
      return null;
    }

    const rowSpanValue = skipRowSpan ? undefined : getRowSpanValue(rowSpans, rowIndex);
    const reachesLastRow = rowSpanValue !== undefined && rowSpanValue > 1 && rowIndex + rowSpanValue >= totalRows;
    const cellClassNames: string = cx({ '!border-b-0': reachesLastRow });

    return (
      <TableCell
        className={cellClassNames}
        key={cell.id}
        columnConfiguration={{
          alignment: cellMeta?.alignment,
          ellipsis: cellMeta?.ellipsis,
          isPartOfMergeByColumn: cellMeta?.isPartOfMergeByColumn ?? false,
          key: columnKey,
          rowSpans: cellMeta?.rowSpans,
          width: widths[columnKey]
        }}
        fixed={fixedDirection}
        fixedOffset={fixedOffset}
        isFirstCell={cellIndex === 0}
        isFixedBoundary={isFixedBoundary}
        isLastCell={cellIndex === cells.length - 1}
        isNested={isNested}
        isSelected={isSelected}
        rowIndex={rowIndex}
        rowSpan={rowSpanValue}
        tableId={tableId}
      >
        {renderCellContent(cell, row, renderExpandIcon)}
      </TableCell>
    );
  };

  const expandedRowClassNames: string = cx(rowClassNames, 'group/row zen-table-row');
  const normalRowClassNames: string = cx(
    rowClassNames,
    'group/row zen-table-row',
    (isNested || isSelected) && 'bg-azure-lightest',
    '[&:last-child_td]:border-b-0'
  );

  // Render row with custom expanded content
  if (hasCustomExpandedContent) {
    return (
      <Fragment key={row.id}>
        <tr className={expandedRowClassNames}>{cells.map((cell, cellIndex) => renderCell(cell, cellIndex, true))}</tr>
        <tr>
          <td className="border-b border-grey-lighter" colSpan={cells.length}>
            <div style={{ position: 'sticky', left: 0, width: 'var(--zen-viewport-width, 100%)' }}>
              {renderExpandedRow(getOriginalRowData(row.original), rowIndex)}
            </div>
          </td>
        </tr>
      </Fragment>
    );
  }

  // Render normal row
  return (
    <tr key={row.id} className={normalRowClassNames}>
      {cells.map((cell, cellIndex) => renderCell(cell, cellIndex))}
    </tr>
  );
}

// Export as FC with generic support
const TableRow = TableRowComponent as <T extends object>(props: TableRowProps<T>) => ReactNode;

export type { TableRowProps };

export default TableRow;
